import cn from 'classnames'
import React from 'react'

import type { IOption } from 'components/uiKit/KitTypes'
import { KitSize } from 'components/uiKit/KitTypes'
import { OptionLabel } from 'components/uiKit/KitUtils'
import { testProps } from 'utils/test/qaData'

import type { ArrayElement, ICheckboxOption, CheckboxValue } from '../Checkbox'
import { isChecked } from '../isChecked'
import * as s from './CheckboxItem.module.scss'
import CheckIcon from './check.svg?react'

export type CheckboxDefaultType = Omit<
  React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>,
  | 'onChange'
  | 'type'
  | 'prefix'
  | 'size'
  | 'ref'
  | 'defaultValue'
  | 'name'
  | 'onBlur'
  | 'onFocus'
  | 'value'
>

interface IProps<T extends CheckboxValue | CheckboxValue[] = CheckboxValue> {
  name: string
  item: IOption<ArrayElement<T>>
  value?: T
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  hideCheckbox?: boolean
  disabled?: boolean
  indeterminate?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  option?: ICheckboxOption<any>
  size?: KitSize
  style?: React.CSSProperties
  testData?: string
  activeColor?: string
  bordered?: boolean
  info?: React.ReactNode
}

const CheckboxItem = <T extends CheckboxValue | CheckboxValue[] = CheckboxValue>(
  {
    item,
    value,
    onChange,
    hideCheckbox,
    disabled,
    name,
    indeterminate,
    option,
    size,
    style,
    testData,
    activeColor,
    bordered,
    info,
    ...rest
  }: IProps<T>,
  ref: React.Ref<HTMLLabelElement>,
) => {
  const checked = isChecked(value, item, option)

  const cnRoot = cn(s.root, size && s[size], {
    [s.checked]: checked,
    [s.disabled]: disabled,
    [s.hideCheckbox]: hideCheckbox,
    [s.bordered]: bordered,
    [s.withInfo]: !!info,
  })

  return (
    <label
      ref={ref}
      {...rest}
      className={cnRoot}
      {...testProps({ el: 'checkboxItem', name, value: item.value, testData })}
    >
      <input
        checked={checked}
        className={s.input}
        disabled={disabled}
        name={name}
        onChange={onChange}
        type='checkbox'
        value={String(item.value)}
      />
      {!hideCheckbox && (
        <span className={s.checkbox}>
          {indeterminate ? (
            <div className={s.indeterminate} />
          ) : (
            <span className={s.checkIcon}>{checked && <CheckIcon />}</span>
          )}
        </span>
      )}
      <div className={s.content}>
        <span className={s.label} style={{ ...style, color: checked ? activeColor : undefined }}>
          <OptionLabel
            disabled={disabled}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            option={item.label ? item : (option as any)}
            size={KitSize.M}
          />
        </span>
        {info && <div className={s.info}>{info}</div>}
      </div>
    </label>
  )
}

export default React.forwardRef(CheckboxItem) as <
  T extends CheckboxValue | CheckboxValue[] = CheckboxValue,
>(
  props: IProps<T> & { ref?: React.Ref<HTMLLabelElement> },
) => React.ReactElement
